/**
 * The negative binomial distribution for a whole number r of successes, under both
 * textbook parameterizations.
 *
 * - "trials": X = the number of trials needed to reach the r-th success,
 *   X = r, r + 1, …; P(X = k) = C(k − 1, r − 1) p^r (1 − p)^(k − r). Mean r/p.
 * - "failures": Y = the number of failures before the r-th success, Y = 0, 1, …;
 *   P(Y = y) = C(y + r − 1, y) p^r (1 − p)^y. Mean r(1 − p)/p. This is R's `dnbinom(y,
 *   size = r, prob = p)` and SciPy's `nbinom(r, p)`.
 *
 * Same experiment, X = Y + r, so everything works from the failure count and shifts by
 * the convention's first value (r trials, or 0 failures). CDF and tails come from the
 * regularised incomplete beta function, never from summing terms.
 */

import { logChoose, regularizedBeta } from "@/lib/stats/special";
import type { DiscreteModel } from "@/lib/tool-kit/stats-distributions/discrete";

export type NegativeBinomialConvention = "trials" | "failures";

/** The smallest possible value: r trials, or 0 failures. */
export function negativeBinomialFirst(
  r: number,
  convention: NegativeBinomialConvention
): number {
  return convention === "trials" ? r : 0;
}

/** The R argument for a value of the chosen convention: `dnbinom` counts failures. */
export function toRFailures(
  k: number,
  r: number,
  convention: NegativeBinomialConvention
): number {
  return k - negativeBinomialFirst(r, convention);
}

/** P(Y = y) for y failures before the r-th success. */
function failuresPmf(y: number, r: number, p: number): number {
  if (!Number.isInteger(y) || y < 0) {
    return 0;
  }
  if (p >= 1) {
    return y === 0 ? 1 : 0;
  }
  return Math.exp(
    logChoose(y + r - 1, y) + r * Math.log(p) + y * Math.log1p(-p)
  );
}

/** P(Y ≤ y) = I_p(r, y + 1). */
function failuresCdf(y: number, r: number, p: number): number {
  if (y < 0) {
    return 0;
  }
  return p >= 1 ? 1 : regularizedBeta(p, r, Math.floor(y) + 1);
}

/** P(Y ≥ y) = I_(1−p)(y, r), evaluated directly so deep tails keep their digits. */
function failuresUpper(y: number, r: number, p: number): number {
  const start = Math.ceil(y);
  if (start <= 0) {
    return 1;
  }
  return p >= 1 ? 0 : regularizedBeta(1 - p, start, r);
}

export function negativeBinomialModel(
  r: number,
  p: number,
  convention: NegativeBinomialConvention
): DiscreteModel {
  const first = negativeBinomialFirst(r, convention);
  const failuresMean = (r * (1 - p)) / p;
  return {
    pmf: (k) => failuresPmf(k - first, r, p),
    cdf: (k) => failuresCdf(k - first, r, p),
    upper: (k) => failuresUpper(k - first, r, p),
    mean: convention === "trials" ? r / p : failuresMean,
    variance: (r * (1 - p)) / (p * p),
    support: [first, Number.POSITIVE_INFINITY],
  };
}

/** The upper end of the search: no real input needs more than a billion failures. */
const SEARCH_LIMIT = 1_000_000_000;

/** The smallest value whose cumulative probability reaches q (0 < q < 1). */
export function negativeBinomialQuantile(
  q: number,
  r: number,
  p: number,
  convention: NegativeBinomialConvention
): number {
  const first = negativeBinomialFirst(r, convention);
  if (p >= 1 || q <= 0) {
    return first;
  }
  let high = 1;
  while (failuresCdf(high, r, p) < q && high < SEARCH_LIMIT) {
    high *= 2;
  }
  let low = -1;
  // Invariant: cdf(low) < q ≤ cdf(high).
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (failuresCdf(middle, r, p) >= q) {
      high = middle;
    } else {
      low = middle;
    }
  }
  return high + first;
}

/**
 * R's `mu` parameterization (mean μ, size) turned into p: `dnbinom(x, size, mu = μ)`
 * equals `dnbinom(x, size, prob = size / (size + μ))`.
 */
export function probFromMean(size: number, mu: number): number {
  return size / (size + mu);
}
